import Vue from "vue";
import VueApollo from "vue-apollo";
import { ApolloLink } from "apollo-link";
import {
  createApolloClient,
  restartWebsockets,
} from "vue-cli-plugin-apollo/graphql-client";
import {
  InMemoryCache,
  IntrospectionFragmentMatcher,
} from "apollo-cache-inmemory";

import EventBus from "@/lib/event-bus";
import loggerLink from "./links/logger-link";
import authLink from "./links/auth-link";
import errorLink from "./links/error-link";
import retryLink from "./links/retry-link";
import httpLink from "./links/http-link";
import introspectionQueryResultData from "@/graphql/fragmentTypes.json";

// Name of the localStorage auth token...
export const AUTH_TOKEN_STORAGE_ITEM = "pai-at";

// Setup GraphQl fragments...
const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
});

// Install the Vue plugin...
Vue.use(VueApollo);

// Removes the auth token from local storage
export function removeAuthToken() {
  window.localStorage.removeItem(AUTH_TOKEN_STORAGE_ITEM);
}

// Gets the access token from local storage
export function getAccessToken() {
  return window.localStorage.getItem(AUTH_TOKEN_STORAGE_ITEM);
}

// Stores the access token in local storage
export function storeAccessToken(token) {
  if (token) {
    window.localStorage.setItem(AUTH_TOKEN_STORAGE_ITEM, token);
  }
}

// Setup our links...
// Taken from https://medium.com/@joanvila/productionizing-apollo-links-4cdc11d278eb
// Error link/refreshing taken from https://able.bio/AnasT/apollo-graphql-async-access-token-refresh--470t1c8
const link = ApolloLink.from([
  loggerLink,
  errorLink(storeAccessToken),
  authLink(getAccessToken),
  retryLink,
  httpLink,
]);

// Config...
const defaultOptions = {
  // You can use `https` for secure connection (recommended in production)
  // httpEndpoint, // not needed as using httpLink
  defaultHttpLink: false,
  wsEndpoint: null,
  // Enable Automatic Query persisting with Apollo Engine
  persisting: false,
  // Use websockets for everything (no HTTP)
  // You need to pass a `wsEndpoint` for this to work
  websocketsOnly: false,
  // Is being rendered on the server?
  ssr: false,
  // Override default apollo link
  link,
  // Set the default options, fetchPolicy etc
  defaultOptions: {
    query: {
      fetchPolicy: "network-only",
      loadingKey: "loading",
    },
    watchQuery: {
      fetchPolicy: "network-only",
      loadingKey: "loading",
    },
  },
  // Override default cache
  cache: new InMemoryCache({ fragmentMatcher }),
};

// Create apollo client
export const { apolloClient } = createApolloClient({
  ...defaultOptions,
});

// Call this in the Vue app file...
export function createProvider(options = {}) {
  // Create vue apollo provider
  const apolloProvider = new VueApollo({
    defaultClient: apolloClient,
    defaultOptions: {
      $query: {
        fetchPolicy: "cache-and-network",
        loadingKey: "loading",
      },
    },
    watchLoading(isLoading, countModifier) {
      const loading = countModifier === 1;
      EventBus.$emit("global-loading", loading);
    },
  });

  return apolloProvider;
}

// Manually call this when user logs in...
export async function onLogin(apolloClient, token) {
  storeAccessToken(token);

  try {
    await apolloClient.resetStore();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log("%cError on cache reset (login)", "color: orange;", e.message);
  }
}
